// ────────────────────────────────────────────────────────────────────────────
// Which VAT treatment line a sale falls on — the FE twin of the Directory's
// `VatTreatmentRules.Resolve`, kept in step by the same cases (see
// `VatTreatmentRulesTests`). The lines and the organisation's choices come
// from `GET /organisationVatTreatments`; this only decides which line.
//
// The customer's VAT country decides almost everything — B2B transport is
// taxed where the customer is established — and the route only matters for a
// customer in the organisation's own area (zero-rated when linked to an export
// or import).
//
// XI is read two ways on purpose: as a CUSTOMER it is UK (transport is a
// service); as a point on the ROUTE it is EU for an EU-registered organisation
// (Windsor Framework — IE→XI is not an export) and UK for a UK one.
// ────────────────────────────────────────────────────────────────────────────
import type { VatTreatmentLine, VatTreatmentMatrix } from './client';

const EU_MEMBERS = new Set([
  'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR',
  'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI',
  'SK',
]);

/** Great Britain, Northern Ireland and the Isle of Man, which shares UK VAT. */
const UK_VAT_AREA = new Set(['GB', 'XI', 'IM']);

const norm = (code: string | null | undefined): string | null => {
  const c = code?.trim().toUpperCase();
  return c ? c : null;
};

/**
 * The matrix line for a sale, or null when it cannot be told: no matrix for the
 * organisation's country, the customer has no VAT country, or (for a customer
 * in the organisation's own area) a route end is unknown.
 */
export function resolveVatTreatmentLine(
  matrix: VatTreatmentMatrix | null | undefined,
  customerVatCountry: string | null | undefined,
  routeStartCountry: string | null | undefined,
  routeEndCountry: string | null | undefined,
): VatTreatmentLine | null {
  if (!matrix?.regime) return null;
  const home = norm(matrix.homeCountryIsoCode);
  const customer = norm(customerVatCountry);
  if (!home || !customer) return null;

  const uk = matrix.regime === 'Uk';
  const customerScope = uk
    ? UK_VAT_AREA.has(customer)
      ? 'Home'
      : 'Outside'
    : customer === home
      ? 'Home'
      : EU_MEMBERS.has(customer)
        ? 'OtherEu'
        : 'Outside';

  const lines = matrix.lines.filter((l) => l.customer === customerScope);
  const any = lines.find((l) => l.route === 'Any');
  if (any) return any;

  const start = norm(routeStartCountry);
  const end = norm(routeEndCountry);
  if (!start || !end) return null;

  const insideForGoods = (c: string) => (uk ? UK_VAT_AREA.has(c) : EU_MEMBERS.has(c) || c === 'XI');
  const route = insideForGoods(start) && insideForGoods(end) ? 'Inside' : 'Outside';
  return lines.find((l) => l.route === route) ?? null;
}

/**
 * How a line is shown to people: `IE-1` for an Irish organisation, `GB-2` for
 * a British one — the organisation's own country, not the internal `EU-1`.
 */
export function vatTreatmentLineLabel(
  matrix: Pick<VatTreatmentMatrix, 'homeCountryIsoCode'> | null | undefined,
  ruleKey: string,
): string {
  const n = ruleKey.split('-')[1] ?? ruleKey;
  const home = norm(matrix?.homeCountryIsoCode);
  return home ? `${home}-${n}` : ruleKey;
}
